import React, { useEffect } from 'react';

// assets

// services

// props

// components
import { ActionButton, Link, Stack, TagPicker } from '@fluentui/react';
import Label from '../../../typography/label';
import Text from '../../../typography/text';
import { IShipResourceShortProps, IShipScheduleResourceShort } from '../../../../props/data/ships';
import ShipsService from '../../../../services/data/ships';
import ShipSchedulesService from '../../../../services/data/ships/shipSchedules';
import moment from 'moment';
import GeneralService from '../../../../services/general';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/pro-light-svg-icons';
import ShipScheduleForm from '../../../operationals/ships/schedules/general/form';
import PermissionsService from '../../../../services/permissions';
import { useStore } from '../../../../stores/root';

type ShipScheduleField = {
    ship?: IShipResourceShortProps;
    selected?: IShipScheduleResourceShort;
    options?: IShipScheduleResourceShort[];
    qs?: string[];
    label?: string;
    hideLabel?: boolean;
    required?: boolean;
    disabled?: boolean;
    allowCreate?: boolean;
    filterExpired?: boolean;
    onChange(selected?: IShipScheduleResourceShort): void;
}

const ShipScheduleField: React.FC<ShipScheduleField> = (props: ShipScheduleField) => {
    const { user } = useStore();
    const [activeSurface, setActiveSurface] = React.useState<string | undefined>();
    const [editingSchedule, setEditingSchedule] = React.useState<IShipScheduleResourceShort | undefined>();
    const [selected, setSelected] = React.useState<IShipScheduleResourceShort | undefined>();

    useEffect(() => {
        setSelected(props.selected);
    }, [props.selected]);

    useEffect(() => {
    }, [props]);

    const _onSearchSchedule = async (keyword: string) => {
        try {
            let list: IShipScheduleResourceShort[] = [];

            if (props.options) {
                list = [...props.options];
            } else {
                const qs: string[] = props.qs ? [...props.qs] : [];
                if (props.ship) { qs.push(`ship_id=${props.ship.id}`) }
                if (keyword && keyword.trim() !== '') { qs.push(`search=${keyword}`) }

                list = await ShipSchedulesService.retrieve(qs.join("&"));
            }

            // Sort: Active schedules first (earliest departure first), then expired schedules (most recent past first)
            list.sort((a, b) => {
                const aExpired = ShipSchedulesService.isExpired(a);
                const bExpired = ShipSchedulesService.isExpired(b);
                if (aExpired && !bExpired) return 1;
                if (!aExpired && bExpired) return -1;
                if (!aExpired && !bExpired) {
                    return moment(a.estimatedDeparture).valueOf() - moment(b.estimatedDeparture).valueOf();
                }
                return moment(b.estimatedDeparture).valueOf() - moment(a.estimatedDeparture).valueOf();
            });

            return list.map((item) => {
                const etdDate = item.estimatedDeparture || item.actualDeparture;
                const etdFormatted = etdDate ? moment(etdDate).format("DD/MM/YYYY") : '';
                const route = (item.originAddress || item.destinationAddress) 
                    ? ` (${item.originAddress || 'POL'} ➔ ${item.destinationAddress || 'POD'})` 
                    : '';
                return {
                    key: item.id,
                    name: etdFormatted ? `Voy #${item.voy}${route} • ETD: ${etdFormatted}` : `Voy #${item.voy}${route}`,
                    metadata: item
                };
            });
        } catch (error) {
            return [];
        }
    }

    return <Stack>
        {props.hideLabel ? null : <Label style={{padding: '3px 0px'}} size={'xsmall'} required={props.required}>{props.label || "Schedule"}</Label>}
        {!selected ? <>
            <TagPicker
                disabled={props.disabled}
                selectedItems={[]}
                removeButtonAriaLabel='Remove'
                itemLimit={1}
                onItemSelected={(item: any) => {
                    const md = item ? (item.metadata as IShipScheduleResourceShort) : undefined;
                    if (!md) return null;

                    const isPast = ShipSchedulesService.isExpired(md);
                    if (isPast && props.filterExpired !== false) {
                        // Tidak bisa dipilih langsung jika tanggal sudah lewat
                        // Buka form update agar user mengupdate tanggal ETD/ETA rute ini
                        setEditingSchedule(md);
                        setActiveSurface('update');
                        return null;
                    }

                    props.onChange(md);
                    return null;
                }}
                onRenderSuggestionsItem={(suggestionProps: any) => {
                    const md = suggestionProps.metadata as IShipScheduleResourceShort;
                    const isPast = ShipSchedulesService.isExpired(md);
                    const etdDate = md.estimatedDeparture || md.actualDeparture;
                    const etdFormatted = etdDate ? moment(etdDate).format("DD/MM/YYYY") : '-';

                    return <Stack
                        styles={{
                            root: {
                                padding: '8px 12px',
                                borderBottom: '1px solid #f0f0f0',
                                width: '100%',
                                backgroundColor: isPast ? '#fafafa' : '#ffffff',
                                cursor: isPast ? 'not-allowed' : 'pointer'
                            }
                        }}
                        horizontalAlign='baseline'
                    >
                        <Stack horizontal horizontalAlign='space-between' verticalAlign='center' styles={{ root: { width: '100%' } }}>
                            <Stack horizontal verticalAlign='center' tokens={{ childrenGap: 8 }}>
                                <Label size={'small'} style={{ fontWeight: 600, color: isPast ? '#666' : '#111' }}>
                                    Voy #{md.voy} • ETD: {etdFormatted}
                                </Label>
                                {isPast ? (
                                    <span style={{ fontSize: 10, fontWeight: 600, background: '#fff2e8', color: '#d4380d', border: '1px solid #ffbb96', padding: '1px 6px', borderRadius: 4 }}>
                                        Lewat Estimasi
                                    </span>
                                ) : (
                                    <span style={{ fontSize: 10, fontWeight: 600, background: '#f6ffed', color: '#389e0d', border: '1px solid #b7eb8f', padding: '1px 6px', borderRadius: 4 }}>
                                        Aktif
                                    </span>
                                )}
                            </Stack>
                            {isPast && props.allowCreate && props.ship ? (
                                <Link
                                    style={{ fontSize: 11, fontWeight: 600, color: '#0958d9' }}
                                    onClick={(e: any) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setEditingSchedule(md);
                                        setActiveSurface('update');
                                    }}
                                >
                                    ✏️ Update Tanggal (ETD/ETA)
                                </Link>
                            ) : null}
                        </Stack>

                        <Stack horizontal verticalAlign='center' styles={{ root: { marginTop: 4, width: '100%' } }}>
                            <Stack styles={{ root: { width: 140 } }} horizontalAlign='baseline' verticalAlign={'baseline'}>
                                <Text size={'small'} style={{ fontWeight: 500, color: isPast ? '#666' : '#222', textAlign: 'start' }}>{md.originAddress || 'POL'}</Text>
                                {md.actualDeparture ? <Text size={'xsmall'} style={{ color: isPast ? '#cf1322' : '#666' }}>{moment(md.actualDeparture).format("DD/MM/YYYY")} (Aktual)</Text> : null}
                                {!md.actualDeparture ? <Text size={'xsmall'} style={{ color: isPast ? '#cf1322' : '#666' }}>{moment(md.estimatedDeparture).format("DD/MM/YYYY")} (Est.)</Text> : null}
                            </Stack>
                            <Stack styles={{ root: { marginLeft: 15, marginRight: 15, color: '#888' } }}><FontAwesomeIcon icon={faArrowRight} /></Stack>
                            <Stack styles={{ root: { width: 140 } }} horizontalAlign='baseline' verticalAlign={'baseline'}>
                                <Text size={'small'} style={{ fontWeight: 500, color: isPast ? '#666' : '#222', textAlign: 'start' }}>{md.destinationAddress || 'POD'}</Text>
                                {md.actualArrival ? <Text size={'xsmall'} style={{ color: '#666' }}>{moment(md.actualArrival).format("DD/MM/YYYY")} (Aktual)</Text> : null}
                                {!md.actualArrival ? <Text size={'xsmall'} style={{ color: '#666' }}>{moment(md.estimatedArrival).format("DD/MM/YYYY")} (Est.)</Text> : null}
                            </Stack>
                        </Stack>

                        {isPast ? (
                            <Stack horizontal horizontalAlign='space-between' verticalAlign='center' styles={{ root: { marginTop: 6, paddingTop: 4, borderTop: '1px dashed #f0f0f0', width: '100%' } }}>
                                <Text size={'xsmall'} style={{ color: '#8c8c8c' }}>
                                    ⚠️ Jadwal sudah lewat, tidak bisa dipilih langsung. Klik baris ini atau tombol di atas untuk perbarui tanggal ETD/ETA.
                                </Text>
                            </Stack>
                        ) : null}
                    </Stack>;
                }}
                onResolveSuggestions={_onSearchSchedule}
                onEmptyResolveSuggestions={() => _onSearchSchedule('')}
            />
            {props.allowCreate && props.ship && PermissionsService.hasPermission(['ships.schedules.create'], user.permissions) ? (
                <Text size={'small'} style={{ marginTop: 4, color: '#666' }}>
                    Mau buat jadwal dengan rute baru? <Link onClick={() => { setActiveSurface('create') }}>+ Buat Jadwal Kapal Baru</Link>
                </Text>
            ) : null}
        </> : null}
        {selected ? <Stack styles={{ root: { padding: 8 } }} className={'card'} horizontal tokens={{ childrenGap: 20 }} horizontalAlign={'space-between'} verticalAlign='center'>
            <Stack tokens={{ childrenGap: 10 }}>
                <Stack horizontal verticalAlign='center' tokens={{ childrenGap: 8 }}>
                    <Label size={'small'}>
                        Voy #{selected.voy} (ETD: {moment(selected.estimatedDeparture || selected.actualDeparture).format("DD/MM/YYYY")})
                    </Label>
                    {ShipSchedulesService.isExpired(selected) ? (
                        <span style={{ fontSize: 10, background: '#f5f5f5', color: '#8c8c8c', border: '1px solid #d9d9d9', padding: '1px 5px', borderRadius: 3 }}>
                            Riwayat Selesai
                        </span>
                    ) : null}
                </Stack>
                <Stack tokens={{ childrenGap: 10 }}>
                    <Stack grow={1}>
                        <Stack horizontal tokens={{ childrenGap: 5 }}>
                            <Label style={{ width: 30 }} size={'xsmall'}>POL: </Label>
                            <Text size={'small'}>{selected.originAddress}</Text>
                        </Stack>
                        <Stack styles={{ root: { marginLeft: 35 } }}>
                            {!selected.actualDeparture ? <Text size={'small'}>{GeneralService.formatDate(moment(selected.estimatedDeparture).toDate())} (Est.)</Text> : null}
                            {selected.actualDeparture ? <Text size={'small'}>{GeneralService.formatDate(moment(selected.actualDeparture).toDate())} (Actual)</Text> : null}
                        </Stack>
                    </Stack>
                    <Stack className={'divider'}> </Stack>
                    <Stack grow={1}>
                        <Stack horizontal tokens={{ childrenGap: 5 }}>
                            <Label style={{ width: 30 }} size={'xsmall'}>POD: </Label>
                            <Text size={'small'}>{selected.destinationAddress}</Text>
                        </Stack>
                        <Stack styles={{ root: { marginLeft: 35 } }}>
                            {!selected.actualArrival ? <Text size={'small'}>{GeneralService.formatDate(moment(selected.estimatedArrival).toDate())} (Est.)</Text> : null}
                            {selected.actualArrival ? <Text size={'small'}>{GeneralService.formatDate(moment(selected.actualArrival).toDate())} (Actual)</Text> : null}
                        </Stack>
                    </Stack>
                </Stack>
            </Stack>
            {!props.disabled ? (
                <Stack horizontal tokens={{ childrenGap: 2 }}>
                    <ActionButton
                        iconProps={{ iconName: "Edit" }}
                        title="Update Tanggal ETD/ETA"
                        onClick={() => {
                            setEditingSchedule(selected);
                            setActiveSurface('update');
                        }}
                    />
                    <ActionButton iconProps={{ iconName: "Delete" }} title="Hapus Pilihan Jadwal" onClick={() => { props.onChange(); }} />
                </Stack>
            ) : null}
        </Stack> : null}
        {activeSurface === 'update' && editingSchedule && (props.ship || (editingSchedule as any).ship) ? <ShipScheduleForm
            shipId={props.ship?.id || (editingSchedule as any).ship?.id}
            scheduleId={editingSchedule.id}
            onDismissed={(refresh, schedule) => {
                setActiveSurface(undefined);
                setEditingSchedule(undefined);
                if (refresh && schedule) {
                    setSelected(schedule);
                    props.onChange(schedule);
                }
            }} /> : null}
        {activeSurface === 'create' && props.ship ? <ShipScheduleForm
            shipId={props.ship?.id}
            onDismissed={(refresh, schedule) => {
                setActiveSurface(undefined);
                if (refresh && schedule) {
                    setSelected(schedule);
                    props.onChange(schedule);
                }
            }} /> : null}
    </Stack>;
};

export default ShipScheduleField;
